import type { TraceAssetSelection } from "../trace";
import { formatRegisteredGridValue } from "./candidates";
import { createTraceGridTransform, type GridControlPoint } from "./transform";

export interface GridFillTarget {
  readonly input: HTMLInputElement;
  readonly isKnownGrid: boolean;
  readonly prompt: string;
}

interface PendingControl {
  readonly kind: "control";
  readonly index: number;
}

interface PendingTarget {
  readonly kind: "target";
  readonly target: GridFillTarget;
}

type PendingClick = PendingControl | PendingTarget | null;

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

function errorMessage(code: string): string {
  switch (code) {
    case "CONTROL_COUNT":
      return "Set all four control points on the trace.";
    case "DEGENERATE_CONTROLS":
      return "The four control points must define a usable map area.";
    case "MIXED_PRECISION":
      return "All control coordinates must use the same number of digits.";
    case "INVALID_OUTPUT_PRECISION":
      return "Choose the required grid precision.";
    default:
      return "Enter 2 to 5 digits for every control easting and northing.";
  }
}

export function mountTraceGridRegistration(
  target: HTMLElement,
  selection: () => TraceAssetSelection,
  fillTargets: readonly GridFillTarget[],
): void {
  const open = element("button", "Resolve grid fields from the trace");
  open.type = "button";
  open.dataset.role = "grid-register-open";
  const status = element(
    "p",
    "Register four known grid intersections, then click each required location.",
  );
  status.dataset.role = "grid-register-status";
  const workspace = element("div");
  workspace.className = "grid-registration";
  target.append(open, status, workspace);

  open.addEventListener("click", () => {
    const trace = selection();
    if (trace.imageUrl === null) {
      status.textContent = "Select the trace image before resolving grids.";
      status.dataset.status = "error";
      return;
    }

    workspace.replaceChildren();
    const instructions = element(
      "p",
      "For each control, enter the printed easting and northing, choose Set point, then click that intersection on the trace. Use equal precision for all eight values.",
    );
    const precisionLabel = element("label", "Required grid precision ");
    const outputPrecision = element("select");
    outputPrecision.dataset.role = "grid-output-precision";
    const choosePrecision = element("option", "Choose precision");
    choosePrecision.value = "";
    outputPrecision.append(choosePrecision);
    for (const precision of [2, 3, 4, 5]) {
      const option = element(
        "option",
        `${String(precision * 2)} figures (${String(precision)} easting + ${String(precision)} northing)`,
      );
      option.value = String(precision);
      outputPrecision.append(option);
    }
    precisionLabel.append(outputPrecision);
    const controlsForm = element("div");
    controlsForm.className = "grid-controls";
    const controlPositions: ({
      readonly x: number;
      readonly y: number;
    } | null)[] = [null, null, null, null];
    const coordinateInputs: {
      readonly easting: HTMLInputElement;
      readonly northing: HTMLInputElement;
      readonly position: HTMLSpanElement;
    }[] = [];
    let pending: PendingClick = null;

    for (let index = 0; index < 4; index += 1) {
      const row = element("div");
      row.className = "grid-control-row";
      row.append(element("strong", `Control ${String(index + 1)}`));
      const easting = element("input");
      easting.inputMode = "numeric";
      easting.placeholder = "Easting";
      easting.pattern = "[0-9]{2,5}";
      easting.dataset.role = "grid-control-easting";
      easting.dataset.index = String(index);
      const northing = element("input");
      northing.inputMode = "numeric";
      northing.placeholder = "Northing";
      northing.pattern = "[0-9]{2,5}";
      northing.dataset.role = "grid-control-northing";
      northing.dataset.index = String(index);
      const setPoint = element("button", "Set point");
      setPoint.type = "button";
      setPoint.dataset.role = "grid-control-set";
      setPoint.dataset.index = String(index);
      const position = element("span", "not placed");
      setPoint.addEventListener("click", () => {
        pending = { kind: "control", index };
        status.textContent = `Click control ${String(index + 1)} on the trace.`;
        delete status.dataset.status;
      });
      coordinateInputs.push({ easting, northing, position });
      row.append(easting, northing, setPoint, position);
      controlsForm.append(row);
    }

    const figure = element("figure");
    figure.className = "grid-registration-figure";
    const image = element("img");
    image.src = trace.imageUrl;
    image.alt = "Locally selected trace for grid registration";
    image.dataset.role = "grid-registration-image";
    figure.append(image);

    const targetControls = element("div");
    targetControls.className = "grid-targets";
    for (const fillTarget of fillTargets) {
      const setTarget = element(
        "button",
        `Set ${fillTarget.prompt} from trace`,
      );
      setTarget.type = "button";
      setTarget.dataset.role = "grid-target-set";
      setTarget.dataset.blankId = fillTarget.input.name;
      setTarget.addEventListener("click", () => {
        pending = { kind: "target", target: fillTarget };
        status.textContent = `Click the location for ${fillTarget.prompt} on the trace.`;
        delete status.dataset.status;
      });
      targetControls.append(setTarget);
    }

    image.addEventListener("click", (event) => {
      if (pending === null) {
        status.textContent =
          "Choose a control or field before clicking the trace.";
        status.dataset.status = "error";
        return;
      }
      const bounds = image.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      const point = {
        x: Math.min(
          1,
          Math.max(0, (event.clientX - bounds.left) / bounds.width),
        ),
        y: Math.min(
          1,
          Math.max(0, (event.clientY - bounds.top) / bounds.height),
        ),
      };
      if (pending.kind === "control") {
        controlPositions[pending.index] = point;
        const row = coordinateInputs[pending.index];
        if (row !== undefined) row.position.textContent = "placed";
        status.textContent = `Control ${String(pending.index + 1)} placed.`;
        status.dataset.status = "ready";
        pending = null;
        return;
      }

      const controls: GridControlPoint[] = [];
      for (let index = 0; index < 4; index += 1) {
        const position = controlPositions[index];
        const row = coordinateInputs[index];
        if (position === null || position === undefined || row === undefined)
          continue;
        controls.push({
          ...position,
          easting: row.easting.value.trim(),
          northing: row.northing.value.trim(),
        });
      }
      const result = createTraceGridTransform(
        controls,
        Number(outputPrecision.value),
      );
      if (!result.ok) {
        status.textContent = errorMessage(result.code);
        status.dataset.status = "error";
        return;
      }
      const projected = result.transform.project(point.x, point.y);
      if (projected === null) {
        status.textContent =
          "That point projects outside the registered grid range. Check the controls.";
        status.dataset.status = "error";
        return;
      }
      pending.target.input.value = formatRegisteredGridValue(
        projected.value,
        pending.target.isKnownGrid,
      );
      pending.target.input.dispatchEvent(new Event("input", { bubbles: true }));
      status.textContent = `${pending.target.prompt} filled. Verify the value against the trace.`;
      status.dataset.status = "ready";
      pending = null;
    });

    workspace.append(
      instructions,
      precisionLabel,
      controlsForm,
      targetControls,
      figure,
    );
    status.textContent =
      "Choose precision, then enter and place all four control points.";
    delete status.dataset.status;
  });
}
